/* LOGGBOKA — uka, og det som mangler i den.
 *
 * ---------------------------------------------------------------------------
 * DETTE ER SKJERMEN PRODUKTET FINNES FOR
 *
 * Den daglige runden er hvordan opptegnelsen blir ført. Her blir den en
 * opptegnelse: hullene får navn, de fire avviksfeltene fylles ut, og uka blir
 * et dokument noen kan sette navnet sitt under.
 *
 * Rekkefølgen er statussidens, som SiteFeeding argumenterer for: ORD FØRST,
 * så det som kan gjøres noe med, og først deretter det som bare kan leses.
 *
 * ---------------------------------------------------------------------------
 * HVER MANGEL ER EN SETNING MATTILSYNET FAKTISK HAR SKREVET
 *
 * De er ikke funnet på. `behandling_uten_velferd` er Firda 5.1.2 nesten
 * ordrett — «Behandlingsloggene vi har sett viser kun lusestatus før og etter
 * behandling, ikke status for fiskevelferd» — og `avvik_uferdig` er 5.1.3:
 * «Avvikshåndteringene i disse sakene retter seg i hovedsak inn mot
 * strakstiltak.» Teksten kommer fra tjenesten, ikke herfra, slik at spørsmålet
 * og svaret aldri kan komme i utakt.
 *
 * ---------------------------------------------------------------------------
 * ALDRI ET SNITT, NOE STED PÅ DENNE SIDEN
 *
 * Laksvel §5.4 forbyr å presentere skår som snitt eller som endring i snitt, og
 * Mattilsynet siterer forbudet med tilslutning i SalMar-rapporten. Hundre fisk
 * på skår 1 som blir nitti på 1 og ti på 3 flytter snittet fra 1,0 til 1,2 —
 * som ser ut som ingenting — mens en tidel av populasjonen nettopp fikk alvorlig
 * skade. Her står tellinger og fordelinger, ingenting annet.
 *
 * RUTINEN ER MERKER OG IKKE PROSENT. Sju dager, fylt eller tomt. Et hull er et
 * hull, og det er hele poenget med figuren: den kan fotograferes og vises fram.
 */
import { useCallback, useState } from 'react'

import {
  ApiError,
  loggbok, loggbokUkePath,
  type LoggbokGap, type LoggbokUke,
} from '../api'
import { ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { Link } from '../router'
import { useApi } from '../useApi'

const DAGER = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn']

function dagMnd(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('nb-NO',
    { day: 'numeric', month: 'short' })
}

function skiftUke(iso: string, uker: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + uker * 7)
  return d.toISOString().slice(0, 10)
}

/** Uka i ord, før noen figur.
 *
 *  SiteFeeding gjør det samme og skriver ned hvorfor: en statusside gjetter
 *  ikke, den skriver «Partial outage, 08:26-08:50, 24 min». Et menneske som
 *  bare rekker én linje skal få den som betyr noe. */
function ord(u: LoggbokUke): string {
  const deler: string[] = []
  const dager = u.rutine.filter((d) => !d.framtid).length
  deler.push(`${u.rounds_done} av ${dager} dager ført`)

  const avvik = u.gaps.filter((g) => g.severity === 'avvik').length
  if (avvik) deler.push(`${avvik} ${avvik === 1 ? 'alvorlig mangel' : 'alvorlige mangler'}`)

  const apne = u.avvik.filter((a) => !a.closed_at).length
  if (apne) deler.push(`${apne} ${apne === 1 ? 'åpent avvik' : 'åpne avvik'}`)

  const n = u.entries.length
  deler.push(`${n} ${n === 1 ? 'hendelse' : 'hendelser'}`)
  return deler.join('. ') + '.'
}

export function SiteLoggbokUke({ id }: { id: string }) {
  const [uke, setUke] = useState<string | null>(null)
  const q = useApi<LoggbokUke>(loggbokUkePath(id, uke ?? undefined))
  const u = q.data

  const [travel, setTravel] = useState(false)
  const [feil, setFeil] = useState<ApiError | Error | null>(null)
  const [aapent, setAapent] = useState<string | null>(null)

  const gjor = useCallback(async (fn: () => Promise<unknown>) => {
    setTravel(true); setFeil(null)
    try {
      await fn()
      await q.reloadAsync()
      setTravel(false)
      return true
    } catch (e) {
      setTravel(false)
      setFeil(e instanceof ApiError ? e : new Error('nettverk'))
      return false
    }
  }, [q])

  if (q.cold) return <Rows n={4} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!u) return null

  return (
    <>
      <PageHead title="Loggbok" tight
        lead={`Uke ${u.iso_week} · ${dagMnd(u.from)}–${dagMnd(u.to)}`} />

      <div className="loggbok-faner">
        <Link className="btn sm ghost"
          to={`/sites/${encodeURIComponent(id)}/loggbok`}>I dag</Link>
        <button type="button" className="btn sm primary" aria-current="page">
          Uka
        </button>
        <span className="grow" />
        <div className="loggbok-dagnav">
          <button type="button" className="btn sm ghost" aria-label="Forrige uke"
            onClick={() => setUke(skiftUke(u.from, -1))}>‹</button>
          {uke && (
            <button type="button" className="btn sm ghost"
              onClick={() => setUke(null)}>Denne uka</button>
          )}
          <button type="button" className="btn sm ghost" aria-label="Neste uke"
            onClick={() => setUke(skiftUke(u.from, 1))}>›</button>
        </div>
      </div>

      {!u.available && (
        <p className="notice">
          {u.reason || 'Loggboka er ikke tilgjengelig akkurat nå.'}
        </p>
      )}
      {feil && <ErrorBox error={feil} />}

      <p className="loggbok-ukeord">{ord(u)}</p>

      {/* Det som mangler. Først, fordi det er det eneste på siden som kan
          gjøres noe med. */}
      {u.gaps.length > 0 && (
        <section className="seksjon">
          <h2 className="seksjon-hode">Det som mangler</h2>
          {u.gaps.map((g, i) => <Mangel key={i} g={g} id={id} />)}
        </section>
      )}

      <section className="seksjon">
        <h2 className="seksjon-hode">Rutinen</h2>
        <div className="uke-rutine">
          {u.rutine.map((d, i) => (
            <div key={d.on} className={`uke-dag${d.framtid ? ' framtid' : ''}`}>
              <span className="uke-dagnavn">{DAGER[i]}</span>
              <span className={`uke-rute${d.runde ? ' fylt' : ''}`}
                title={d.runde ? 'Runde ført' : 'Ingen runde'} />
              <span className="uke-tall muted">
                {d.hendelser || ''}
                {d.apne ? <em> +{d.apne}</em> : null}
              </span>
            </div>
          ))}
        </div>
        <p className="muted">
          Et fylt merke er en dag runden ble ført. Tallet under er hendelser
          skrevet den dagen; et plusstall er kort som står ubesvart.
        </p>
      </section>

      {u.avvik.length > 0 && (
        <section className="seksjon">
          <h2 className="seksjon-hode">Avvik</h2>
          {u.avvik.map((a) => (
            <Avvik key={a.entry_id} a={a} u={u} id={id} travel={travel}
              aapen={aapent === a.entry_id}
              onAapne={() => setAapent(aapent === a.entry_id ? null : a.entry_id)}
              gjor={gjor} />
          ))}
        </section>
      )}

      {u.per_merd.length > 0 && (
        <section className="seksjon">
          <h2 className="seksjon-hode">Merd for merd</h2>
          <div className="uke-merder">
            {u.per_merd.map((p) => (
              <div key={p.pen_id} className="uke-merd">
                <strong>{p.pen_no}</strong>
                <span className="muted">
                  {p.n ? `${p.n} ført` : 'ingenting ført'}
                  {p.avvik ? ` · ${p.avvik} avvik` : ''}
                </span>
                <span className={`uke-rute sm${p.laksvel ? ' fylt' : ''}`}
                  title={p.laksvel ? 'Velferdsskåret denne uka'
                    : 'Ikke velferdsskåret denne uka'} />
              </div>
            ))}
          </div>
          <p className="muted">
            Tellinger, aldri et snitt. Laksvel-protokollen §5.4 forbyr å
            presentere skår som snittverdier.
          </p>
        </section>
      )}

      <section className="seksjon">
        <h2 className="seksjon-hode">Uka i ord</h2>
        {u.entries.length === 0 && <p className="muted">Ingenting ført.</p>}
        {u.entries.map((e) => (
          <p key={e.entry_id} className="loggbok-kvittering">
            <span className="muted">{dagMnd(e.on)}</span>{' '}
            <strong>{u.katalog.forslag[e.kind]?.navn
              ?? u.katalog.skrevet[e.kind]?.navn ?? e.kind}</strong>
            {e.answer && <> — {e.answer}</>}
            {e.pens.length > 0 && (
              <span className="muted"> · merd {e.pens.map((p) => p.pen_no).join(', ')}</span>
            )}
            {e.note && <> {e.note}</>}
          </p>
        ))}
      </section>

      <div className="loggbok-avslutt">
        {u.signed
          ? (
            <p className="muted">
              Signert av {u.signed.by} {dagMnd(u.signed.at.slice(0, 10))}.
            </p>
          )
          : (
            <button type="button" className="btn primary" disabled={travel}
              onClick={() => gjor(() => loggbok.signer(id, { uke: u.from }))}>
              Signer uka
            </button>
          )}
        <p className="muted">
          Å signere endrer ingen data. Det sier at et navngitt menneske har lest
          uka og står bak den.
        </p>
      </div>
    </>
  )
}

function Mangel({ g, id }: { g: LoggbokGap; id: string }) {
  const href = g.goto === 'dag' && g.on
    ? `/sites/${encodeURIComponent(id)}/loggbok?on=${g.on}`
    : null
  return (
    <p className={`uke-mangel ${g.severity}`}>
      {g.on && <span className="muted">{dagMnd(g.on)} </span>}
      {g.text}
      {href && <> <Link to={href}>Åpne dagen</Link></>}
    </p>
  )
}

/** Ett avvik, med de fire feltene synlige som fylt eller tomt.
 *
 *  Her bor timinuttersjobben, med vilje langt unna det daglige minuttet. Feltet
 *  som mangler blir spurt om hver uke til det er der — det er forskjellen på et
 *  system som fanger opp rotårsaker og et som bare noterer strakstiltak. */
function Avvik({ a, u, id, travel, aapen, onAapne, gjor }: {
  a: LoggbokUke['avvik'][number]
  u: LoggbokUke
  id: string
  travel: boolean
  aapen: boolean
  onAapne: () => void
  gjor: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const [felt, setFelt] = useState({
    strakstiltak: a.strakstiltak ?? '',
    rotaarsak: a.rotaarsak ?? '',
    forebyggende: a.forebyggende ?? '',
    evaluering: a.evaluering ?? '',
  })

  const kategori = u.katalog.avvik_kategorier
    .find((c) => c.key === a.category)?.label ?? a.category
  const forfalt = !a.closed_at && a.due_on
    && a.due_on < new Date().toISOString().slice(0, 10)

  const lagre = (close: boolean) => gjor(() => loggbok.avvik(id, a.entry_id, {
    on_date: a.on,
    strakstiltak: felt.strakstiltak.trim() || null,
    rotaarsak: felt.rotaarsak.trim() || null,
    forebyggende: felt.forebyggende.trim() || null,
    evaluering: felt.evaluering.trim() || null,
    close,
  }))

  const FELT: [keyof typeof felt, string][] = [
    ['strakstiltak', 'Strakstiltak — hva ble gjort med én gang'],
    ['rotaarsak', 'Rotårsak — hvorfor skjedde det'],
    ['forebyggende', 'Forebyggende tiltak — hva hindrer at det skjer igjen'],
    ['evaluering', 'Evaluering — virket det'],
  ]

  return (
    <article className={`uke-avvik${forfalt ? ' forfalt' : ''}`}>
      <button type="button" className="loggbok-radhode" aria-expanded={aapen}
        onClick={onAapne}>
        <span className="muted">{dagMnd(a.on)}</span>
        <span className="grow">
          <strong>{kategori}</strong>
          {a.headline && <> — {a.headline}</>}
        </span>
        <span className="chip">
          {a.closed_at ? 'lukket' : `${a.utfylt} av 4`}
        </span>
      </button>

      {aapen && (
        <div className="stack loggbok-radkropp">
          {FELT.map(([k, etikett]) => (
            <label key={k} className="loggbok-notat">
              <span className="muted">{etikett}</span>
              <textarea rows={2} value={felt[k]} disabled={!!a.closed_at}
                onChange={(e) => setFelt({ ...felt, [k]: e.target.value })} />
            </label>
          ))}

          {!a.closed_at && (
            <div className="row">
              <button type="button" className="btn" disabled={travel}
                onClick={() => lagre(false)}>Lagre</button>
              {/* Serveren nekter å lukke uten rotårsak og forebyggende tiltak
                  — det er en CHECK i databasen, ikke en regel i denne
                  knappen. Knappen er derfor ikke deaktivert: setningen som
                  kommer tilbake forklarer bedre enn en grå knapp gjør. */}
              <button type="button" className="btn primary" disabled={travel}
                onClick={() => lagre(true)}>Lukk avviket</button>
            </div>
          )}
          {a.closed_at && (
            <p className="muted">
              Lukket av {a.closed_by} {dagMnd(a.closed_at.slice(0, 10))}.
            </p>
          )}
        </div>
      )}
    </article>
  )
}
